/**
 * Dialog showing canonical composer details with linked canonical pieces.
 */

import { useState } from 'react'
import {
  useCanonicalComposerDetails,
  useDeleteCanonicalComposer,
} from '../../hooks/useCanonicalComposers'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@brent/core-ui/ui'
import { Button } from '@brent/core-ui/ui'
import { Card, CardContent } from '@brent/core-ui/ui'
import { Alert, AlertDescription } from '@brent/core-ui/ui'
import { Badge } from '@brent/core-ui/ui'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@brent/core-ui/ui'
import { Loader2, Edit, Trash2, ArrowUpRight, Music, Calendar } from 'lucide-react'
import { useToast } from '../../hooks/use-toast'
import { formatDate } from '../../utils/format'
import type { CanonicalComposer } from '../../types/canonical-composer'
import { EditCanonicalComposerDialog } from './EditCanonicalComposerDialog'
import { PromoteCanonicalComposerDialog } from './PromoteCanonicalComposerDialog'

interface CanonicalComposerDetailsDialogProps {
  composer: CanonicalComposer
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CanonicalComposerDetailsDialog({
  composer,
  open,
  onOpenChange,
}: CanonicalComposerDetailsDialogProps) {
  const { data, isLoading, error } = useCanonicalComposerDetails(composer.canonical_composer_id)
  const deleteMutation = useDeleteCanonicalComposer()
  const { toast } = useToast()

  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  const handleDeleteConfirm = async () => {
    try {
      await deleteMutation.mutateAsync(composer.canonical_composer_id)

      toast({
        title: 'Composer deleted',
        description: `"${composer.name}" has been deleted.`,
      })

      setDeleteConfirmOpen(false)
      onOpenChange(false)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to delete composer',
        description: err instanceof Error ? err.message : 'An error occurred',
      })
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{composer.name}</DialogTitle>
            <DialogDescription>
              {composer.birth_year && (
                <span>
                  {composer.birth_year}–{composer.death_year ?? 'present'}
                </span>
              )}
              {composer.birth_year && ' • '}
              <span>
                {composer.piece_count} canonical {composer.piece_count === 1 ? 'piece' : 'pieces'}
              </span>
            </DialogDescription>
          </DialogHeader>

          {/* Info section */}
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Birth Year:</span>{' '}
                <span className="font-medium">{composer.birth_year ?? '—'}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Death Year:</span>{' '}
                <span className="font-medium">{composer.death_year ?? '—'}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Pieces:</span>{' '}
                <Badge variant={composer.piece_count >= 1 ? 'default' : 'secondary'}>
                  {composer.piece_count}
                </Badge>
              </div>
              <div>
                <span className="text-muted-foreground">Created:</span>{' '}
                <span className="font-medium">
                  <Calendar className="inline h-3 w-3 mr-1" />
                  {formatDate(composer.created_at)}
                </span>
              </div>
            </div>

            {composer.notes && (
              <div className="text-sm">
                <span className="text-muted-foreground">Notes:</span> <span>{composer.notes}</span>
              </div>
            )}
          </div>

          {/* Linked canonical pieces */}
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertDescription>Failed to load details: {error.message}</AlertDescription>
            </Alert>
          )}

          {data && (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium mb-3">Linked Canonical Pieces</h3>
                <div className="space-y-2">
                  {data.canonical_pieces.map((piece) => (
                    <Card key={piece.canonical_piece_id}>
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-2">
                            <Music className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">
                              {piece.work_title || 'Untitled work'}
                            </span>
                          </div>
                          <Badge variant="secondary" className="font-mono">
                            {piece.suggestion_count}{' '}
                            {piece.suggestion_count === 1 ? 'suggestion' : 'suggestions'}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  {data.canonical_pieces.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No canonical pieces linked to this composer.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex items-center gap-2 pt-4 border-t">
            <Button variant="outline" size="sm" onClick={() => setEditDialogOpen(true)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPromoteDialogOpen(true)}>
              <ArrowUpRight className="h-4 w-4 mr-2" />
              Promote to Wikidata
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setDeleteConfirmOpen(true)}
              disabled={composer.piece_count > 0}
              title={
                composer.piece_count > 0
                  ? 'Cannot delete a composer with linked pieces'
                  : 'Delete this composer'
              }
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete canonical composer?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete &quot;{composer.name}&quot;. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit dialog */}
      <EditCanonicalComposerDialog
        composer={composer}
        open={editDialogOpen}
        onOpenChange={(isOpen) => {
          setEditDialogOpen(isOpen)
          if (!isOpen) {
            // Close details dialog after editing (data may have changed)
            onOpenChange(false)
          }
        }}
      />

      {/* Promote dialog */}
      <PromoteCanonicalComposerDialog
        composer={composer}
        open={promoteDialogOpen}
        onOpenChange={(isOpen) => {
          setPromoteDialogOpen(isOpen)
          if (!isOpen) {
            // Close details dialog after promoting (composer will be deleted)
            onOpenChange(false)
          }
        }}
      />
    </>
  )
}
